import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FilePenLine,
  Search,
  Users2,
  Plus,
  ToggleLeft,
  ToggleRight,
  Download
} from "lucide-react";
import StatsBox from "../common/statsBoxv2";
import constant from "../../constant";
import FileSaver from 'file-saver';
import Papa from 'papaparse';

const PartiesTable = ({ parties, onDelete, onEdit }) => {
  const [statusFilter, setStatusFilter] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [localParties, setLocalParties] = useState(parties);
  const [selectedParties, setSelectedParties] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({ totalReceivable: 0, totalPayable: 0 });


  const userRole = localStorage.getItem("role");

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.error("No token found");
          return;
        }

        const response = await fetch(`${constant.REACT_APP_API_URL}/party/dashboard`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const result = await response.json();
          setDashboardStats(result.data);
        } else {
          const errorResult = await response.json();
          console.error("Failed to fetch dashboard stats:", errorResult);
          // Optionally alert user: alert(errorResult.message || "Failed to fetch dashboard stats");
        }
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
        // Optionally alert user: alert("Error fetching dashboard stats");
      }
    };

    fetchDashboardStats();
  }, []);


  const handleToggleStatus = async (partyId, currentStatus) => {
    setLocalParties((prevParties) =>
      prevParties.map((party) =>
        party._id === partyId ? { ...party, isActive: !currentStatus } : party
      )
    );

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No authentication token found");
        return;
      }

      const response = await fetch(
        `${constant.REACT_APP_API_URL}/party/${partyId}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isActive: !currentStatus }),
        }
      );

      if (response.ok) {
        onEdit(partyId, !currentStatus);
      } else {
        setLocalParties((prevParties) =>
          prevParties.map((party) =>
            party._id === partyId ? { ...party, isActive: currentStatus } : party
          )
        );
        const result = await response.json();
        throw new Error(result.message || "Failed to toggle party status");
      }
    } catch (error) {
      console.error(error);
      alert(error.message);
    }
  };

  const handleSelectAll = () => {
    if (selectedParties.length === filteredParties.length) {
      setSelectedParties([]);
    } else {
      setSelectedParties(filteredParties.map((party) => party._id));
    }
  };

  const handleRowSelect = (partyId) => {
    setSelectedParties((prevSelected) =>
      prevSelected.includes(partyId)
        ? prevSelected.filter((id) => id !== partyId)
        : [...prevSelected, partyId]
    );
  };

  const filteredParties = localParties.filter((party) => {
    const matchesStatus =
      statusFilter === "all"
        ? true
        : statusFilter === "active"
          ? party.isActive
          : !party.isActive;

    const matchesSearch =
      party.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      party.mobile.includes(searchQuery) ||
      party.partyType.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType =
      typeFilter === "all" || party.partyType.toLowerCase() === typeFilter;

    return matchesStatus && matchesSearch && matchesType;
  });

  const handleDownload = async () => {
    const token = localStorage.getItem('token');
    const adminSelectedUser = localStorage.getItem('adminSelectedUser');

    if (!token) {
      alert("No authentication token found");
      return;
    }

    if (!adminSelectedUser) {
      alert("No user selected for download.");
      return;
    }

    try {
      const response = await fetch(
        `${constant.REACT_APP_API_URL}/party/download/${adminSelectedUser}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result && result.data) {
          const csvData = convertJsonToCsv(result.data);
          const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
          FileSaver.saveAs(blob, 'parties.csv');
        } else {
          alert("No data received for download.");
        }
      } else {
        const errorResult = await response.json();
        throw new Error(errorResult.message || "Failed to download parties");
      }
    } catch (error) {
      console.error("Download error:", error);
      alert(error.message);
    }
  };

  const convertJsonToCsv = (jsonData) => {
    const csv = Papa.unparse({
      fields: ["name", "partyType", "mobile", "gstNo", "billingAddress", "shippingAddress", "createdAt"],
      data: jsonData.map(item => ({
        name: item.name,
        partyType: item.partyType,
        mobile: item.mobile,
        gstNo: item.gstNo,
        billingAddress: `${item.billingAddress.name}, ${item.billingAddress.streetAddress}, ${item.billingAddress.city}, ${item.billingAddress.state}, ${item.billingAddress.country}, ${item.billingAddress.pincode}`,
        shippingAddress: `${item.shippingAddress.name}, ${item.shippingAddress.streetAddress}, ${item.shippingAddress.city}, ${item.shippingAddress.state}, ${item.shippingAddress.country}, ${item.shippingAddress.pincode}`,
        createdAt: item.createdAt,
      }))
    });
    return csv;
  };


  return (
    <div className="max-w-8xl mx-auto px-0 lg:px-8 py-8">

      {/* Header Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Parties</h1>
            <p className="mt-1 text-xs text-gray-500">
              Total parties ({parties.length})
            </p>
          </div>
          <div className="flex gap-3">
            {userRole === 'admin' ? (
              <button
                onClick={handleDownload}
                className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-colors duration-150"
              >
                <Download size={18} className="mr-2" />
                Download
              </button>
            ) : (
              <Link
                to="/parties/add"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors duration-150"
              >
                <Plus size={18} className="mr-2" />
                Add Party
              </Link>
            )}

          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-12 md:gap-6 gap-4 mb-8">
          <StatsBox className="col-span-12 md:col-span-12" title="Total Receivable (to collect)" text={dashboardStats?.totalReceivable || 0}></StatsBox>
          {/* <StatsBox title="Total Payable (to pay)" text={dashboardStats?.totalPayable || 0}></StatsBox> */}
        </div>

        {/* Search and Filter Section */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
          <div className="relative w-full sm:w-96">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search parties..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
            />
          </div>
          <div className="flex gap-4 items-center bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="block w-40 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs font-medium transition-all duration-200"
            >
              <option value="all">Select Party Type</option>
              <option value="customer">Customer</option>
              <option value="supplier">Supplier</option>
            </select>
            <div className="flex gap-2">
              {["all", "active", "inactive"].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`
                    px-4
                    py-2
                    rounded-md
                    text-xs
                    font-medium
                    transition-all
                    duration-200
                    focus:outline-none
                    focus:ring-2
                    focus:ring-blue-300
                    ${statusFilter === status
                      ? "bg-blue-100 text-blue-700 ring-2 ring-blue-200"
                      : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-50 hover:shadow-sm"
                    }
                  `}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>



      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
        <div className="min-w-full overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedParties.length === filteredParties.length}
                    onChange={handleSelectAll}
                    className="form-checkbox h-4 w-4 text-blue-600 cursor-pointer"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mobile
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  GST No (Shipping)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  GST No (Billing)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Party Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                {userRole === 'admin' ? (
                  ''
                ) : (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredParties.map((party) => (
                <tr
                  key={party._id}
                  className="hover:bg-gray-50 transition-colors duration-150"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedParties.includes(party._id)}
                      onChange={() => handleRowSelect(party._id)}
                      className="form-checkbox h-4 w-4 text-blue-600 cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link
                      to={`/parties/${party._id}`}
                      className="flex items-center"
                    >
                      <div className="h-8 w-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-3">
                        <Users2 size={16} />
                      </div>
                      <span className="text-xs font-medium capitalize text-gray-900 hover:text-blue-600">
                        {party.name}
                      </span>
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {party.mobile}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {party.toCollect-party.toPay}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {party.shippingAddress.gstNo}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {party.billingAddress.gstNo}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {party.partyType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                        ${party.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                        }
                      `}
                    >
                      {party.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {userRole === 'admin' ? (
                    ''
                  ) : (
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                      <div className="flex items-center space-x-3">
                        <Link
                          to={`/parties/edit/${party._id}`}
                          className="text-blue-600 hover:text-blue-900 transition-colors duration-150"
                        >
                          <FilePenLine size={18} />
                        </Link>
                        <button
                          onClick={() => handleToggleStatus(party._id, party.isActive)}
                          className="text-gray-600 hover:text-gray-900 transition-colors duration-150"
                        >
                          {party.isActive ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                        </button>
                      </div>
                    </td>
                  )}


                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PartiesTable;